import { createSlice } from '@reduxjs/toolkit';
import * as RouteService from '../../services/routes';
import {
  hasInvalidStopNumbers,
  hasRouteRequests,
  prepareNoStopNumberRouteStops,
  prepareRoute,
  updateStopInStopList,
  updateStopsWithValidStopNumber,
} from '../../models/route';
import { getRoutesList } from '../selectors/routes';
import { getBulkStopThreshold, getSelectedDepot } from '../selectors/app';
import { NotificationType } from '../../features/Notifier';
import {
  DISMISS,
  LOCKED_BY,
  NO,
  OUTSTANDING_PARCEL_REQUEST_WARNING,
  OUTSTANDING_PARCEL_REQUESTS_WARNING,
  SUCCESSFUL_RESYNC_ROUTE_NOTIFICATION,
  UNABLE_TO_UNLOCK_ROUTE,
  UNLOCK,
  UNLOCK_ROUTE_WARNING,
  YES,
} from '../../constants/strings';
import {
  formatRouteDetailData,
  getCurrentRouteId,
  getDriverLocations,
  getRouteDetail,
  getRouteStops,
  isCurrentUserLock,
  isRouteReadyOrOpen,
  isRouteStatusSuitableForResync,
  isSaveRouteEnabled,
  isScheduleRequired,
} from '../selectors/route';
import {
  getCurrentUser,
  getUserDepots,
  getUserMetadata,
} from '../selectors/user';
import {
  handleErrorNotification,
  hideNotificationsExceptUnlock,
  NotificationKeys,
  showNotification,
  showOutstandingParcelRequestsNotification,
  showResyncRouteNotification,
} from '../actions/notifications';
import {
  changeRouteOpportunityTime,
  updateListRoute,
  updateRouteInList,
} from './routes';
import { endLoadingProcess, startLoadingProcess } from './loader';
import {
  CHECK_CHOICE_ROUTE,
  LOAD_ROUTE_DETAILS_PROCESS,
  LOAD_SAVE_PROCESS,
  RESYNC_ROUTE_PROCESS,
} from '../../enums/loaderProcess';
import { clearAll } from '../actions/ui/routeDetail';
import { selectedDepotDowngradesProps } from '../../models/depot';
import {
  containsOutstandingParcelRequests,
  getErrorMessage,
  isValidationError,
  parseJSONfromError,
} from '../../utils/errors';
import RouteStatus from '../../enums/RouteStatus';
import {
  getDowngradesProps,
  isRouteSchedulingOpen,
} from '../selectors/schedule';
import { getOpenModalRef } from '../selectors/modal';
import {
  finishScheduleRoute,
  resetAllChanges,
  resetAllRouteEntity,
} from '../actions/schedule';
import { setOpenModalType } from './modal';
import {
  resetStopDataChanged,
  setRouteOutstandingRequestToParcel,
} from '../../features/routeDetail/model';
import {
  getPreparedStops,
  getSelectedPreparedStops,
} from '../selectors/ui/routeDetail';
import { moveStops } from '../../models/moveStops';
import { resetAllRequestsExceptDeferred } from '../actions/request';
import { uncheckBulkStops } from '../../models/stop';
import { formatMessage } from '../../utils/string';
import { hideNotifications } from './notifications';
import { updateSelectedStops } from './ui/routeDetail/stopFilters';

export const unlockSnackbarKey = 'unlockSnackbarKey';

const initialState = {
  currentUserLock: false,
  routeDetail: null,
  originalRouteDetail: null,
  scheduleRequired: false,
  transferRouteId: null,
  transferFromRouteId: null,
  requestOrder: 0,
  isSubmitting: false,
  saveRouteEnable: false,
  initialDowngradeAttempts: null,
  fetchError: null,
};

const routeSlice = createSlice({
  name: 'route',
  initialState,
  reducers: {
    completeRouteFetch(state, action) {
      state.routeDetail = action.payload;
      state.originalRouteDetail = action.payload;
      state.fetchError = null;
      state.initialDowngradeAttempts = null;
      state.scheduleRequired = action.payload?.scheduleRequired;
    },
    completeRouteFetchWithError(state, action) {
      state.routeDetail = null;
      state.originalRouteDetail = null;
      state.fetchError = action.payload;
      state.initialDowngradeAttempts = null;
      state.scheduleRequired = false;
    },
    setCurrentUserLock(state, action) {
      state.currentUserLock = action.payload;
    },
    setRouteDetail(state, action) {
      state.routeDetail = action.payload;
    },
    setRequests(state, action) {
      state.routeDetail.request = action.payload;
    },
    setRequestStatus(state, action) {
      state.isSubmitting = action.payload;
    },
    setOriginalRouteDetail(state, action) {
      state.originalRouteDetail = action.payload;
    },
    setScheduleRequired(state, action) {
      state.scheduleRequired = action.payload;
    },
    setSaveRouteEnable(state, action) {
      state.saveRouteEnable = action.payload;
    },
    updateStop(state, action) {
      state.routeDetail.stop = updateStopInStopList(
        state.routeDetail.stop,
        action.payload
      );
    },
    addStop(state, action) {
      state.routeDetail.stop.push(action.payload);
    },
    setStops(state, action) {
      state.routeDetail.stop = action.payload;
    },
    setTransferredStops(state, action) {
      if (state.routeDetail) {
        state.routeDetail.transferredStop = action.payload;
      }
    },
    setDriverLocations(state, action) {
      state.routeDetail.driverLocations = action.payload;
    },
    incrementRequestOrder(state) {
      state.requestOrder += 1;
    },
    resetRequestOrder(state) {
      state.requestOrder = 0;
    },
    setTransferRouteId(state, action) {
      state.transferRouteId = action.payload;
    },
    setTransferFromRouteId(state, action) {
      state.transferFromRouteId = action.payload;
    },
    setInitialDowngradeAttempts(state, action) {
      state.initialDowngradeAttempts = action.payload;
    },
    setOriginalStopsData(state, action) {
      state.routeDetail.originalStopsData = action.payload;
    },
    setRouteChecksum(state, action) {
      if (state.routeDetail) {
        state.routeDetail.checksum = action.payload;
      }
    },
    clearRouteDetail(state) {
      state.routeDetail = null;
      state.originalRouteDetail = null;
      state.scheduleRequired = false;
      state.initialDowngradeAttempts = null;
      state.fetchError = null;
    },
    updateRouteStatus(state, action) {
      state.originalRouteDetail.status = action.payload;
      state.routeDetail.status = action.payload;
    },
    updateRouteManuallyChanged(state, action) {
      state.routeDetail.routeManuallyChanged = action.payload;
    },
  },
});

export const controlLockedByNotification = (routeLockedBy) => (dispatch) => {
  if (routeLockedBy) {
    dispatch(showLockedByNotification(routeLockedBy));
  } else {
    dispatch(hideNotifications([unlockSnackbarKey]));
  }
};

export const updateCurrentUserLockForRoute =
  (routeLockedBy) => (dispatch, getState) => {
    const { username } = getUserMetadata(getState());
    const isLockedByCurrentUser = routeLockedBy === username;

    if (routeLockedBy && isLockedByCurrentUser) {
      dispatch(setCurrentUserLock(true));
    } else {
      dispatch(setCurrentUserLock(false));
    }

    dispatch(controlLockedByNotification(routeLockedBy));
  };

const handleStopNumberRecalculation = (payload) => {
  const routeProcessedInSaturn =
    payload.status === RouteStatus.LOADING ||
    payload.status === RouteStatus.DOWNLOADED;

  // Skip recalculation for routes already processed in Saturn or those with active requests
  if (routeProcessedInSaturn || hasRouteRequests(payload)) {
    return payload;
  }

  if (!payload.stopNumbersSet) {
    const { originalStopsData, updatedStops } = prepareNoStopNumberRouteStops(
      payload.stop
    );
    return { ...payload, stop: updatedStops, originalStopsData };
  }

  if (hasInvalidStopNumbers(payload?.stop)) {
    const { originalStopsData, updatedStops } = updateStopsWithValidStopNumber(
      payload.stop
    );
    return { ...payload, stop: updatedStops, originalStopsData };
  }

  return payload;
};

export const updateRouteDetail = (payload) => (dispatch) => {
  const updatedPayload = handleStopNumberRecalculation(payload);

  dispatch(completeRouteFetch(updatedPayload));
  dispatch(updateCurrentUserLockForRoute(updatedPayload?.routeLockedBy));
};

export const fetchRouteDetail =
  (depotCode, routeId) => async (dispatch, getState) => {
    const state = getState();
    const depots = getUserDepots(state);

    dispatch(startLoadingProcess(LOAD_ROUTE_DETAILS_PROCESS));
    dispatch(clearAll());

    const { routePlannerDowngrades = false } = selectedDepotDowngradesProps(
      depots,
      depotCode
    );
    try {
      let payload = await RouteService.getRouteDetail(
        depotCode,
        routeId,
        routePlannerDowngrades
      );
      dispatch(updateRouteDetail(payload));
    } catch (error) {
      const isResyncRouteEnabled =
        process.env.REACT_APP_RESYNC_EMPTY_COLLECTION_ROUTE &&
        isValidationError(error);
      if (isResyncRouteEnabled) {
        dispatch(
          showResyncRouteNotification({
            onConfirm: () => onResyncCollectionsInRoute(routeId),
            error,
          })
        );
      } else {
        dispatch(handleErrorNotification(error));
      }
      dispatch(completeRouteFetchWithError(error));
    } finally {
      dispatch(endLoadingProcess(LOAD_ROUTE_DETAILS_PROCESS));
    }
  };

export const hideAllNotifications = () => (dispatch) => {
  dispatch(hideNotifications([unlockSnackbarKey, 'lock-unlock-message']));
};

export const showLockedByNotification = (lockedBy) => (dispatch, getState) => {
  const state = getState();
  const currentUser = getCurrentUser(state);
  dispatch(hideAllNotifications());
  dispatch(
    showNotification({
      key: unlockSnackbarKey,
      type:
        currentUser.metadata.routePlannerUnlock &&
        !isCurrentUserLock(state) &&
        NotificationType.ACTION,
      message: `${LOCKED_BY} ${lockedBy}`,
      buttonText: UNLOCK,
      onConfirm: async () => {
        await dispatch(forceUnlockRoute());
      },
    })
  );
};

export const forceUnlockRoute = () => async (dispatch) =>
  await dispatch(unlockRoute(true));

// TODO: move all logic into custom hook for lock and unlock route, clear actions
export const lockRoute = (defaultRoutDetail) => async (dispatch, getState) => {
  let message;
  const state = getState();
  const currentUserLock = isCurrentUserLock(state);
  const { username } = getUserMetadata(state);
  const routeDetail = defaultRoutDetail || getRouteDetail(state);
  const depotCode = getSelectedDepot(state);
  const route = getRoutesList(state)[routeDetail.routeId];

  if (currentUserLock && !defaultRoutDetail) {
    return true;
  }

  try {
    const response = await RouteService.lockRoute(
      depotCode,
      username,
      routeDetail,
      routeDetail?.originalStopsData
    );

    const { checksum, lockFailReason, routeStatus } = response.data.route[0];

    if (lockFailReason) {
      message = lockFailReason;
      return false;
    }

    if (routeDetail?.originalStopsData) {
      dispatch(setOriginalStopsData([]));
    }

    if (!defaultRoutDetail) {
      dispatch(setRouteChecksum(checksum));
      dispatch(setCurrentUserLock(true));
      dispatch(updateRouteStatus(routeStatus));
      dispatch(updateRouteManuallyChanged(true));
      dispatch(showLockedByNotification(username));
    }

    dispatch(
      updateListRoute({
        ...route,
        checksum,
        routeLockedBy: username,
        routeManuallyChanged: true,
      })
    );

    return true;
  } catch {
    message = 'Unable to lock route, please try again';
    return false;
  } finally {
    message &&
      dispatch(
        showNotification({
          key: 'lock-unlock-message',
          type: NotificationType.ACTION,
          message,
          buttonText: DISMISS,
        })
      );
  }
};

export const unlockRoute =
  (forceUnlock = false, defaultRouteDetail) =>
  async (dispatch, getState) => {
    const state = getState();
    const originalRouteDetail = getRouteDetail(state);
    const routeDetail = defaultRouteDetail || originalRouteDetail;
    if (!routeDetail) {
      return;
    }
    const isRouteDetailOpen =
      originalRouteDetail &&
      routeDetail.routeId === originalRouteDetail.routeId;

    const depotCode = getSelectedDepot(state);
    const { username } = getUserMetadata(state);
    const currentUserLock = isCurrentUserLock(state);

    const isLocked = routeDetail?.routeLockedBy || currentUserLock;
    if (!isLocked) {
      return routeDetail?.checksum;
    }
    const route = getRoutesList(state)[routeDetail.routeId];
    try {
      const {
        data: { route: routeResult },
      } = await RouteService.unlockRoute(
        depotCode,
        username,
        routeDetail,
        forceUnlock
      );

      const { checksum, unlockFailReason } = routeResult[0];
      if (unlockFailReason) {
        await Promise.reject(unlockFailReason);
      } else {
        if (!defaultRouteDetail || isRouteDetailOpen) {
          dispatch(hideNotifications([unlockSnackbarKey]));
          await dispatch(setCurrentUserLock(false));
        }
        await dispatch(
          updateListRoute({
            ...route,
            checksum,
            routeLockedBy: null,
          })
        );
        isRouteDetailOpen &&
          (await dispatch(
            setRouteDetail({
              ...(defaultRouteDetail ? originalRouteDetail : routeDetail),
              checksum,
              routeLockedBy: null,
            })
          ));
        return checksum;
      }
    } catch (error) {
      dispatch(
        showNotification({
          key: 'lock-unlock-message',
          type: NotificationType.ACTION,
          message: typeof error === 'string' ? error : UNABLE_TO_UNLOCK_ROUTE,
          buttonText: DISMISS,
        })
      );
      return routeDetail.checksum;
    }
  };

export const onResyncRoute = () => async (dispatch, getState) => {
  const state = getState();
  const depotCode = getSelectedDepot(state);
  const routeId = getCurrentRouteId(state);
  const { routePlannerDowngrades = false } = getDowngradesProps(state);
  const modalRef = getOpenModalRef(state);
  dispatch(startLoadingProcess(RESYNC_ROUTE_PROCESS));
  dispatch(hideNotificationsExceptUnlock());
  try {
    let payload = await RouteService.resyncRouteDetail(
      depotCode,
      routeId,
      routePlannerDowngrades
    );
    dispatch(resetAllRouteEntity());
    isRouteSchedulingOpen(state) && dispatch(finishScheduleRoute());
    dispatch(setOpenModalType(null));
    modalRef && modalRef.current.onClose();
    await dispatch(clearAll());
    await dispatch(updateRouteDetail(payload));
    dispatch(setRouteChecksum(payload.checksum));
    dispatch(
      showNotification({
        type: NotificationType.ACTION,
        message: SUCCESSFUL_RESYNC_ROUTE_NOTIFICATION,
        buttonText: DISMISS,
        key: NotificationKeys.RESYNC_SUCCESSFUL,
      })
    );
  } catch (error) {
    dispatch(handleErrorNotification(error));
    dispatch(setOpenModalType(null));
  } finally {
    dispatch(endLoadingProcess(RESYNC_ROUTE_PROCESS));
  }
};

export const highlightStopWithOutstandingParcelRequests =
  (parcelsData) => async (dispatch, getState) => {
    await dispatch(resetAllChanges());
    const state = getState();
    const stops = getRouteStops(state);
    const modalRef = getOpenModalRef(state);
    const processedParcels = parcelsData.reduce(
      (acc, { parcelCode, routeCode }) => {
        if (!acc[parcelCode]) {
          acc[parcelCode] = [];
        }
        acc[parcelCode].push(routeCode);
        return acc;
      },
      {}
    );
    const { stopsToUpdate, affectedStops } = setRouteOutstandingRequestToParcel(
      stops,
      processedParcels
    );
    dispatch(setStops(stopsToUpdate));
    dispatch(updateSelectedStops(affectedStops));
    dispatch(hideNotificationsExceptUnlock());
    modalRef && modalRef.current.onClose();
  };

export const onResyncCollectionsInRoute =
  (routeId) => async (dispatch, getState) => {
    const state = getState();
    const depotCode = getSelectedDepot(state);
    const { routePlannerDowngrades = false } = getDowngradesProps(state);
    const modalRef = getOpenModalRef(state);
    dispatch(startLoadingProcess(RESYNC_ROUTE_PROCESS));
    dispatch(hideNotificationsExceptUnlock());
    try {
      await RouteService.resyncRouteDetail(
        depotCode,
        routeId,
        routePlannerDowngrades
      );
      dispatch(setOpenModalType(null));
      modalRef && modalRef.current.onClose();
      dispatch(
        showNotification({
          type: NotificationType.ACTION,
          message: SUCCESSFUL_RESYNC_ROUTE_NOTIFICATION,
          buttonText: DISMISS,
          key: NotificationKeys.RESYNC_SUCCESSFUL,
        })
      );
    } catch (error) {
      dispatch(handleErrorNotification(error));
      dispatch(setOpenModalType(null));
    } finally {
      dispatch(endLoadingProcess(RESYNC_ROUTE_PROCESS));
    }
  };

export const moveSelectedStops =
  ({ direction, separatorStopNumber }) =>
  async (dispatch, getState) => {
    const currentUserLock = await dispatch(lockRoute());

    if (!currentUserLock) {
      return;
    }

    const state = getState();
    const { status } = getRouteDetail(state);
    const stop = getPreparedStops(state);
    const selectedStops = getSelectedPreparedStops(state);

    const updatedStops = moveStops({
      stop,
      selectedStops,
      status,
      direction,
      separatorStopNumber,
    });

    dispatch(setStops(updatedStops));
    dispatch(setScheduleRequired(true));
    dispatch(setSaveRouteEnable(isRouteReadyOrOpen(state)));
  };

export const updateOriginalRouteDetail = () => (dispatch, getState) => {
  const state = getState();
  const routeDetail = getRouteDetail(state);
  dispatch(setOriginalRouteDetail(routeDetail));
};

export const getRouteDataForSave = () => (dispatch, getState) => {
  const state = getState();
  const stops = getRouteStops(state);
  const bulkStopThreshold = getBulkStopThreshold(state);
  const updatedStopList = uncheckBulkStops(stops, bulkStopThreshold);

  dispatch(setStops(updatedStopList));

  // don't change 'getState()' to 'state' because of state mutation!
  return formatRouteDetailData(getState());
};

export const saveErrorHandler = (error) => (dispatch, getState) => {
  const state = getState();
  const isResyncRouteEnabled =
    isRouteStatusSuitableForResync(state) && isValidationError(error);
  const isOutstandingParcelRequestsError =
    containsOutstandingParcelRequests(error);
  if (isResyncRouteEnabled) {
    return dispatch(
      showResyncRouteNotification({
        onConfirm: () => dispatch(onResyncRoute()),
        error,
      })
    );
  }
  if (isOutstandingParcelRequestsError) {
    const errorMessage = getErrorMessage(error);
    const parcelsInfo = parseJSONfromError(errorMessage);
    if (parcelsInfo) {
      const message =
        parcelsInfo.length > 1
          ? OUTSTANDING_PARCEL_REQUESTS_WARNING
          : formatMessage(
              OUTSTANDING_PARCEL_REQUEST_WARNING,
              parcelsInfo[0].parcelCode,
              parcelsInfo[0].routeCode
            );
      return dispatch(
        showOutstandingParcelRequestsNotification({
          onConfirm: () =>
            dispatch(highlightStopWithOutstandingParcelRequests(parcelsInfo)),
          message,
        })
      );
    }
  }
  return dispatch(
    showNotification({
      type: NotificationType.ACTION,
      message: getErrorMessage(error),
      buttonText: DISMISS,
    })
  );
};

export const saveRoute = () => async (dispatch, getState) => {
  const state = getState();
  const depotCode = getSelectedDepot(state);
  const routeId = getCurrentRouteId(state);
  const routeData = dispatch(getRouteDataForSave());
  try {
    dispatch(startLoadingProcess(LOAD_SAVE_PROCESS));
    const { data } = await RouteService.saveRoute(
      depotCode,
      routeId,
      routeData
    );
    const { checksum } = data;
    checksum && dispatch(setRouteChecksum(checksum));
    await dispatch(
      updateRouteInList({
        routeId,
        updatedData: {
          scheduleRequired: routeData.route.scheduleRequired,
        },
      })
    );

    dispatch(resetDataAfterSave());

    await dispatch(unlockRoute(false));
    dispatch(
      changeRouteOpportunityTime({
        opportunityTime: routeData.route.totalRouteOpportunityTime,
        routeId,
      })
    );
    dispatch(updateOriginalRouteDetail());
    dispatch(setSaveRouteEnable(false));
    return true;
  } catch (error) {
    dispatch(saveErrorHandler(error));
    return false;
  } finally {
    dispatch(endLoadingProcess(LOAD_SAVE_PROCESS));
  }
};

export const createRoute =
  (formValues, multiRunRoute) => async (dispatch, getState) => {
    const state = getState();
    const depot = getSelectedDepot(state);
    const route = prepareRoute(formValues, multiRunRoute);

    try {
      await RouteService.createRoute(depot, route);
      return true;
    } catch (error) {
      dispatch(
        showNotification({
          type: NotificationType.ACTION,
          message: getErrorMessage(error),
          buttonText: DISMISS,
          key: 'new-route-time-overlaps-another-route',
        })
      );

      return false;
    }
  };

export const showUnlockNotification = (onConfirm) => (dispatch) => {
  dispatch(
    showNotification({
      key: NotificationKeys.UNLOCK_ROUTE_NOTIFICATION,
      onCloseText: NO,
      onConfirmText: YES,
      type: NotificationType.ACTIONS,
      message: UNLOCK_ROUTE_WARNING,
      onConfirm,
    })
  );
};

export const handleUnlockRoute =
  (routePlannerUnlock, route) => (dispatch, getState) => {
    const state = getState();
    const routeDetail = getRouteDetail(state);
    const isRouteDetailOpen =
      routeDetail && routeDetail.routeId === route.routeId;
    const currentUserLock = isCurrentUserLock(state);
    const saveRouteEnable = isSaveRouteEnabled(state);
    const scheduleRequired = isScheduleRequired(state);
    const isUnsavedData =
      currentUserLock && (saveRouteEnable || scheduleRequired);
    if (isRouteDetailOpen && isUnsavedData) {
      return dispatch(
        showUnlockNotification(() => {
          dispatch(unlockRoute(routePlannerUnlock, route));
          dispatch(
            hideNotifications([NotificationKeys.UNLOCK_ROUTE_NOTIFICATION])
          );
        })
      );
    } else {
      return dispatch(unlockRoute(routePlannerUnlock, route));
    }
  };

export const manuallyLockChange =
  (routeDetail) => async (dispatch, getState) => {
    const { routeLockedBy } = routeDetail;
    const { username, routePlannerUnlock } = getUserMetadata(getState());

    if (routeLockedBy && (routeLockedBy === username || routePlannerUnlock)) {
      await dispatch(handleUnlockRoute(routePlannerUnlock, routeDetail));
    } else {
      await dispatch(lockRoute(routeDetail));
    }
  };

export const getWaveChoiceRouteId = (id) => async (dispatch, getState) => {
  try {
    dispatch(startLoadingProcess(CHECK_CHOICE_ROUTE));
    const depotCode = getSelectedDepot(getState());
    const { choiceRoute } = await RouteService.getWaveChoiceRouteId(
      depotCode,
      id
    );
    return choiceRoute;
  } catch (error) {
    dispatch(
      showNotification({
        type: NotificationType.ACTION,
        message: getErrorMessage(error),
        buttonText: DISMISS,
        key: 'choice-route',
      })
    );
    return null;
  } finally {
    dispatch(endLoadingProcess(CHECK_CHOICE_ROUTE));
  }
};

export const partiallyCaddedStops = (routeId) => async (dispatch, getState) => {
  try {
    const depotCode = getSelectedDepot(getState());
    return await RouteService.getPartiallyCaddedStops(depotCode, routeId);
  } catch (error) {
    dispatch(
      showNotification({
        type: NotificationType.ACTION,
        message: getErrorMessage(error),
        buttonText: DISMISS,
        key: 'choice-route',
      })
    );
    return null;
  }
};

export const closeTote = () => async (dispatch, getState) => {
  const state = getState();
  const depotCode = getSelectedDepot(state);
  const routeDetail = getRouteDetail(state);
  const route = getRoutesList(state)[routeDetail.routeId];
  try {
    await RouteService.closeTote(depotCode, route.routeCode);
  } catch (error) {
    dispatch(
      showNotification({
        type: NotificationType.ACTION,
        message: error.error.message,
        buttonText: DISMISS,
        key: 'cannot-close-the-tote',
      })
    );
  }
};

export const updateDriverLocation =
  (location) => async (dispatch, getState) => {
    const driverLocations = getDriverLocations(getState());
    if (
      Array.isArray(driverLocations) &&
      (!driverLocations.length ||
        driverLocations[driverLocations.length - 1].timestamp <
          location.timestamp)
    ) {
      dispatch(setDriverLocations([...driverLocations, location]));
    }
  };

export const resetStopData = () => (dispatch, getState) => {
  const stops = getRouteStops(getState());
  const updatedStops = resetStopDataChanged(stops);
  dispatch(setStops(updatedStops));
};

export const resetAllTransferFromRouteEntity = () => (dispatch) => {
  dispatch(setTransferRouteId());
  dispatch(setTransferFromRouteId());
  dispatch(setTransferredStops([]));
};

export const resetDataAfterSave = () => (dispatch) => {
  dispatch(resetAllRequestsExceptDeferred());
  dispatch(resetStopData());
  dispatch(clearAll());
  dispatch(resetRequestOrder());
  dispatch(setInitialDowngradeAttempts(null));
  dispatch(resetAllTransferFromRouteEntity());
};

export const {
  setCurrentUserLock,
  setRouteDetail,
  setOriginalRouteDetail,
  setRequests,
  setRequestStatus,
  setScheduleRequired,
  setSaveRouteEnable,
  updateStop,
  addStop,
  setStops,
  incrementRequestOrder,
  resetRequestOrder,
  setTransferRouteId,
  setTransferFromRouteId,
  setInitialDowngradeAttempts,
  clearRouteDetail,
  setOriginalStopsData,
  setRouteChecksum,
  updateRouteStatus,
  updateRouteManuallyChanged,
  completeRouteFetch,
  completeRouteFetchWithError,
  setTransferredStops,
  setDriverLocations,
} = routeSlice.actions;

export default routeSlice.reducer;
